import { useState, useEffect } from 'react';

export type RolType = 'cliente' | 'admin' | 'administrador' | 'repartidor' | 'accionista';

// Interfaz para el usuario de Django
interface DjangoUser {
  id: number;
  username: string;
  email: string;
  rol?: string;
  profile?: {
    rol?: string;
    es_admin?: boolean;
    es_cliente?: boolean;
    es_repartidor?: boolean;
  };
  es_admin?: boolean;
  es_cliente?: boolean;
  es_repartidor?: boolean;
}

interface UserWithRole {
  user: DjangoUser | null;
  usuario: DjangoUser | null;
  rol: RolType;
  isAdmin: boolean;
  isCliente: boolean;
  isRepartidor: boolean;
  loading: boolean;
}

/**
 * Hook para obtener el rol del usuario desde Django backend
 */
export const useUserRole = (djangoUser: DjangoUser | null): UserWithRole => {
  const [rol, setRol] = useState<RolType>('cliente');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      if (!djangoUser) {
        setRol('cliente');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Obtener el rol del usuario de Django
        // Prioridad: profile.rol > rol directo > es_admin flag
        let userRol: RolType = 'cliente';
        
        if (djangoUser.profile?.rol) {
          userRol = djangoUser.profile.rol as RolType;
        } else if (djangoUser.rol) {
          userRol = djangoUser.rol as RolType;
        } else if (djangoUser.es_admin || djangoUser.profile?.es_admin) {
          userRol = 'admin';
        } else if (djangoUser.es_repartidor || djangoUser.profile?.es_repartidor) {
          userRol = 'repartidor';
        } else if (djangoUser.es_cliente || djangoUser.profile?.es_cliente) {
          userRol = 'cliente';
        }

        setRol(userRol);
      } catch (error) {
        console.error('Error al cargar datos del usuario:', error);
        setRol('cliente');
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [djangoUser]);

  const isAdmin = 
    rol === 'admin' || 
    rol === 'administrador' || 
    djangoUser?.es_admin === true || 
    djangoUser?.profile?.es_admin === true;

  const isCliente = 
    rol === 'cliente' || 
    djangoUser?.es_cliente === true || 
    djangoUser?.profile?.es_cliente === true;

  const isRepartidor = 
    rol === 'repartidor' || 
    djangoUser?.es_repartidor === true || 
    djangoUser?.profile?.es_repartidor === true;

  return {
    user: djangoUser,
    usuario: djangoUser,
    rol,
    isAdmin,
    isCliente,
    isRepartidor,
    loading,
  };
};

/**
 * Hook para verificar si el usuario tiene permisos de admin
 */
export const useRequireAdmin = (djangoUser: DjangoUser | null) => {
  const { isAdmin, loading } = useUserRole(djangoUser);
  
  useEffect(() => {
    if (!loading && !isAdmin && djangoUser) {
      console.warn('⚠️ Acceso denegado: Se requieren permisos de administrador');
      // Aquí podrías redirigir o mostrar un mensaje
    }
  }, [isAdmin, loading, djangoUser]);

  return { isAdmin, loading };
};
